import {
    PermissionValues,
    useCurrentAccount,
    useRoutineFilter,
} from '@gripp/shared-logic';
import { BaseColumnCheckbox, getGroupColumn } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text } from 'react-native';

export const useColumnsRoutine = (
  showCheckboxColumn: boolean,
  assetsButton: (data: any) => JSX.Element,
  isProducerWorkspace = false,
  addonsButton?: (data: any) => JSX.Element
) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper();
  const [columns, setColumns] = useState<ColumnDef<unknown, never>[]>([]);
  const { data: routineFilter } = useRoutineFilter();
  const { account } = useCurrentAccount();
  const showImpersonateWorkspace = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const allColumns: Record<string, ColumnDef<unknown, never>> = useMemo(
    () => ({
      selected: columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: { style: styles.checkboxCell },
      }),
      name: columnHelper.accessor('name', {
        cell: (info) => {
          const name = info.getValue();
          return <Text style={styles.cellText}>{name}</Text>;
        },
        header: () => t('routine.list.name'),
        meta: {
          sortable: true,
          style: styles.nameCell,
          headerStyle: styles.nameCell,
        },
      }),
      type: columnHelper.accessor('type', {
        cell: (info) => {
          const routineType = info.getValue();
          return (
            <Text style={styles.cellText}>
              {t(`routines.types.${routineType}`)}
            </Text>
          );
        },
        header: () => t('routine.list.type'),
        meta: {
          sortable: false,
          style: styles.typeCell,
          headerStyle: styles.typeCell,
        },
      }),
      assetsCount: columnHelper.accessor('assetsCount', {
        cell: (info) => {
          const assetsCount = info.getValue();
          const row = info.row.original;
          return assetsButton({
            assetsCount: assetsCount,
            routineId: row['id'],
            name: row['name'],
          });
        },
        header: () => t('routine.filter.columns.assetsCount'),
        meta: {
          sortable: true,
          style: styles.numberCell,
          headerStyle: styles.numberCell,
        },
      }),
      addonsCount: columnHelper.accessor('addonsCount', {
        cell: (info) => {
          const addonsCount = info.getValue();
          const row = info.row.original;
          return addonsButton
            ? addonsButton({
                addonsCount: addonsCount ?? 0,
                routineId: row['id'],
                name: row['name'],
              })
            : null;
        },
        header: () => t('routine.filter.columns.addonsCount'),
        meta: {
          sortable: true,
          style: styles.numberCell,
          headerStyle: styles.numberCell,
        },
      }),
      allAssetsCount: columnHelper.accessor('allAssetsCount', {
        cell: (info) => {
          const allAssetsCount = info.getValue();
          return <Text style={styles.cellText}>{allAssetsCount}</Text>;
        },
        header: () => t('routine.filter.columns.allAssetsCount'),
        meta: {
          sortable: true,
          style: styles.numberCell,
          headerStyle: styles.numberCell,
        },
      }),
      submissionsCount: columnHelper.accessor('submissionsCount', {
        cell: (info) => <Text style={styles.cellText}>{info.getValue() ?? 0}</Text>,
        header: () => t('routine.filter.columns.submissionsCount'),
        meta: {
          sortable: true,
          style: styles.numberCell,
          headerStyle: styles.numberCell,
        },
      }),
      groups: columnHelper.accessor('groups', {
        cell: (info) => getGroupColumn(info, () => Promise.resolve()),
        header: () => t('routine.filter.columns.groups'),
        meta: {
          sortable: false,
        },
      }),
      lastSubmitted: columnHelper.accessor('lastSubmitted', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text style={styles.cellText}>{date.fromNow()}</Text>;
          }
        },
        header: () => t('routine.filter.columns.lastSubmitted'),
        meta: { sortable: true },
      }),
      updatedAt: columnHelper.accessor('updatedAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text style={styles.cellText}>{date.fromNow()}</Text>;
          }
        },
        header: () => t('routine.filter.columns.updatedAt'),
        meta: { sortable: true },
      }),
      createdAt: columnHelper.accessor('createdAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text style={styles.cellText}>{date.fromNow()}</Text>;
          }
        },
        header: () => t('routine.filter.columns.createdAt'),
        meta: { sortable: true },
      }),
    }),
    [routineFilter]
  );

  useEffect(() => {
    if (!routineFilter) return;

    const columnList = [];
    if (showCheckboxColumn) {
      columnList.push(allColumns.selected);
    }

    columnList.push(allColumns.name);
    if (showImpersonateWorkspace && routineFilter.columns?.includes('type')) {
      columnList.push(allColumns.type);
    }
    const { assetsCount, addonsCount, allAssetsCount, type, submissionsCount, groups, ...rest } = allColumns || {};
    if (routineFilter.columns?.includes('assetsCount') && isProducerWorkspace) {
      columnList.push(allColumns.assetsCount);
    }
    if (routineFilter.columns?.includes('addonsCount') && addonsButton) {
      columnList.push(allColumns.addonsCount);
    }
    if (routineFilter.columns?.includes('allAssetsCount')) {
      columnList.push(allColumns.allAssetsCount);
    }
    if (routineFilter.columns?.includes('submissionsCount')) {
      columnList.push(allColumns.submissionsCount);
    }
    if (routineFilter.columns?.includes('groups')) {
      columnList.push(allColumns.groups);
    }
    // push the remaining columns based on filter
    Object.keys(rest)
      .filter((key) => routineFilter.columns?.includes(key))
      .forEach((key) => columnList.push(allColumns[key]));

    setColumns(columnList);
  }, [routineFilter, allColumns, showCheckboxColumn, isProducerWorkspace, addonsButton]);

  return columns;
};

const styles = StyleSheet.create({
  routinesContainer: {
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
  emailText: {
    overflow: 'hidden',
  },
  assetsCount: {
    width: 30,
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  numberCell: {
    maxWidth: 120,
  },
  nameCell: {
    flex: 1,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  typeCell: {
    maxWidth: 250,
  },
});
